import { ReactNode, useState } from 'react';

import { Button, ButtonProps, Spinner } from '@chakra-ui/react';
import { Buffer, Workbook } from 'exceljs';
import { BsDownload } from 'react-icons/bs';
import { toast } from 'react-toastify';

import { Deal } from '../../gql/dealGql';
import { Deal as GeneratedDeal } from '../../gql/generated/graphql';

import { GetComponentParams } from '../shared/Table/types';

import { logger } from '../../libs/Logger';

type ExportDealsToXlsxButtonProps = ButtonProps & {
  deals: (Deal | GeneratedDeal)[];
  columns: (
    | {
        header: string;
        getValue: (d: Deal | GeneratedDeal) => string | number | undefined | ReactNode;
        getComponent?: never;
      }
    | {
        header: string;
        getValue?: never;
        getComponent: (params: GetComponentParams<Deal | GeneratedDeal>) => ReactNode;
      }
  )[];
  dealType?: 'closed' | 'booted';
  onFetchAllDeals?: () => Promise<(Deal | GeneratedDeal)[]>;
};

const ExportDealsToXlsxButton = ({
  deals,
  columns,
  dealType = 'closed',
  onFetchAllDeals,
  ...button
}: ExportDealsToXlsxButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const saveByteArray = (reportName: string, byte: Buffer) => {
    const blob = new Blob([byte], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    const fileName = reportName;
    link.download = fileName;
    link.click();
  };

  const exportToXlsx = (dealsToExport: (Deal | GeneratedDeal)[]) => {
    const headers = columns.map(({ header }) => ({ header, key: header }));
    const rows = dealsToExport.map((deal) =>
      Object.assign(
        {},
        ...columns.map(({ header, getValue, getComponent }) => ({
          [header]: getValue ? getValue(deal) : getComponent({ item: deal }),
        })),
      ),
    );

    const workbook = new Workbook();
    const sheet = workbook.addWorksheet('Sheet1');
    sheet.columns = headers;
    rows.forEach((row) => {
      sheet.addRow(row);
    });
    workbook.xlsx
      .writeBuffer()
      .then((data) => saveByteArray(`${dealType}_deals.xlsx`, data))
      .catch((err) => logger.error('ExportDealsToXlsxButton.tsx', '', null, err));
  };

  const handleDownload = async () => {
    if (onFetchAllDeals) {
      setIsLoading(true);
      try {
        const allDeals = await onFetchAllDeals();
        exportToXlsx(allDeals);
      } catch (err) {
        const friendlyMessage = 'Failed to fetch all deals. Please refresh the page and try again.';
        toast.error(friendlyMessage);
        logger.error('ExportDealsToXlsxButton.tsx', friendlyMessage, null, err);
      } finally {
        setIsLoading(false);
      }
    } else {
      exportToXlsx(deals);
    }
  };

  return (
    <Button
      {...button}
      leftIcon={isLoading ? <Spinner size="sm" /> : <BsDownload size={16} />}
      onClick={handleDownload}
      variant="secondary"
      size="smWithIconLeft"
      float="right"
      isDisabled={isLoading}
    >
      {isLoading ? 'LOADING...' : 'DOWNLOAD'}
    </Button>
  );
};

export default ExportDealsToXlsxButton;
